import React, { useEffect, useMemo, useRef, useState } from 'react';
import { AlertTriangle, Clock, FileText, History, Loader2, Save, Users, X } from 'lucide-react';
import type { OrgMember } from '../../../hooks/useOrgMembers';
import type { LstExecucao, LstMotivoParada, LstRevisaoAta, LstRotinaDoDia, LstStatus } from '../types';
import { LST_CATEGORY_STYLES, LST_FREQUENCY_LABELS } from '../types';
import { formatTimeHHmm } from '../services/lstMatrixHourly';
import { SwiExecutionSections } from './swi/SwiExecutionSections';
import { isAtaColetivaContext } from '../utils/lstActivityKind';
import {
  decodeMotivoParada,
  kamishibaiToLstStatus,
  lstStatusToKamishibai,
  type KamishibaiSymbol,
} from '../utils/kamishibaiStatus';
import { KamishibaiActionButton, KamishibaiSymbolBadge } from './kamishibai/KamishibaiSymbol';
import { ProcessStoppedBadge, ProcessStoppedControl } from './kamishibai/ProcessStoppedControl';
import {
  NonConformityEscalationPanel,
  type CriticalProblemPayload,
  type QuickActionPayload,
} from './kamishibai/NonConformityEscalationPanel';
import { resolveTemplateIndicator } from '../utils/lstIndicator';
import type { Indicator, IndicatorSnapshot } from '../../../types/indicator';
import { assessRoutineIndicator } from '../services/ielService';
import { templateHasLinkedIndicator } from '../utils/lstIndicator';
import { RotinaStatusBadge } from './RotinaStatusBadge';

export interface RotinaStatusSubmitPayload {
  status: LstStatus;
  justificativaDesvio?: string | null;
  observacao?: string | null;
  quickAction?: QuickActionPayload;
  criticalProblem?: CriticalProblemPayload;
  indicatorRealizedValue?: number | null;
  motivoParada?: LstMotivoParada;
}

export interface DesvioDoDiaItem {
  title: string;
  justificativa: string | null;
  isCritical?: boolean;
}

interface RotinaContextDrawerProps {
  open: boolean;
  rotina: LstRotinaDoDia | null;
  displayStatus: LstStatus | 'pendente';
  organizationId: string;
  userId: string;
  members: Array<{ id: string; name: string }>;
  indicators: Indicator[];
  execucoes?: LstExecucao[];
  periodStart?: string;
  periodEnd?: string;
  indicatorSnapshots?: IndicatorSnapshot[];
  liderados?: OrgMember[];
  revisoes?: LstRevisaoAta[];
  revisoesLoading?: boolean;
  desviosDoDia?: DesvioDoDiaItem[];
  saving?: boolean;
  savingAta?: boolean;
  resolveUserName: (userId: string) => string;
  onClose: () => void;
  onSubmitStatus: (payload: RotinaStatusSubmitPayload) => Promise<boolean>;
  onSaveAta?: (payload: {
    notes: string;
    lideradoUserId: string | null;
    markRealizado: boolean;
  }) => Promise<boolean>;
}

const inputCls =
  'h-8 w-full rounded-md border border-slate-200 bg-white px-2 text-[12px] text-slate-700 outline-none focus:border-slate-500 dark:border-slate-700 dark:bg-slate-900/40 dark:text-slate-200';
const labelCls = 'mb-1 block text-[10px] font-semibold uppercase tracking-wide text-slate-400';

/**
 * Sidebar lateral direita unificada — rotina padrão, reunião de camada e escalonamento.
 */
export const RotinaContextDrawer: React.FC<RotinaContextDrawerProps> = ({
  open,
  rotina,
  displayStatus,
  userId,
  members,
  indicators,
  execucoes = [],
  periodStart,
  periodEnd,
  indicatorSnapshots = [],
  liderados = [],
  revisoes = [],
  revisoesLoading = false,
  desviosDoDia = [],
  saving = false,
  savingAta = false,
  resolveUserName,
  onClose,
  onSubmitStatus,
  onSaveAta,
}) => {
  const [pendingSymbol, setPendingSymbol] = useState<KamishibaiSymbol | null>(null);
  const [deviationText, setDeviationText] = useState('');
  const [observacaoText, setObservacaoText] = useState('');
  const [indicatorInput, setIndicatorInput] = useState('');
  const [ataNotes, setAtaNotes] = useState('');
  const [lideradoId, setLideradoId] = useState('');
  const deviationRef = useRef<HTMLDivElement>(null);

  const isAtaMode = rotina ? isAtaColetivaContext(rotina.template) : false;

  useEffect(() => {
    if (!open || !rotina) return;
    setPendingSymbol(null);
    setDeviationText(rotina.execucao?.justificativa_desvio ?? '');
    setObservacaoText(rotina.execucao?.observacao ?? '');
    setIndicatorInput(
      rotina.execucao?.indicator_realized_value != null
        ? String(rotina.execucao.indicator_realized_value)
        : '',
    );
    setAtaNotes(rotina.execucao?.observacao ?? '');
    setLideradoId(liderados[0]?.id ?? '');
  }, [open, rotina?.template.id, liderados]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  useEffect(() => {
    if (pendingSymbol !== 'triangle') return;
    deviationRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, [pendingSymbol]);

  const indicatorMeta = useMemo(() => {
    if (!rotina) return null;
    return resolveTemplateIndicator(rotina.template, indicators);
  }, [rotina, indicators]);

  const linkedAssessment = useMemo(() => {
    if (!rotina || !templateHasLinkedIndicator(rotina.template)) return null;
    const start = periodStart ?? rotina.data;
    const end = periodEnd ?? rotina.data;
    return assessRoutineIndicator(
      rotina.template,
      execucoes,
      indicators,
      indicatorSnapshots,
      start,
      end,
    );
  }, [rotina, execucoes, indicators, indicatorSnapshots, periodStart, periodEnd]);

  const historico = useMemo(
    () => [...revisoes].sort((a, b) => b.created_at.localeCompare(a.created_at)).slice(0, 8),
    [revisoes],
  );

  if (!open || !rotina) return null;

  const category = rotina.template.category ?? 'apoio';
  const requiresIndicator = indicatorMeta?.hasLink ?? false;
  const currentSymbol = lstStatusToKamishibai(
    displayStatus === 'pendente' ? null : displayStatus,
  );
  const isParado = displayStatus === 'processo_parado';
  const motivoParada = decodeMotivoParada(rotina.execucao?.justificativa_desvio);

  const submitExtras = {
    observacao: observacaoText.trim() || null,
  };

  const handleSymbolSelect = async (symbol: KamishibaiSymbol) => {
    if (symbol === 'triangle') {
      setPendingSymbol('triangle');
      return;
    }

    if (symbol === 'O' && requiresIndicator) {
      const parsed = Number(indicatorInput.replace(',', '.'));
      if (!Number.isFinite(parsed)) {
        setPendingSymbol('O');
        return;
      }
      const ok = await onSubmitStatus({
        status: 'realizado',
        indicatorRealizedValue: parsed,
        justificativaDesvio: null,
        ...submitExtras,
      });
      if (ok && isAtaMode && ataNotes.trim().length >= 10 && onSaveAta) {
        await onSaveAta({ notes: ataNotes.trim(), lideradoUserId: lideradoId || null, markRealizado: true });
      }
      if (ok) onClose();
      return;
    }

    const ok = await onSubmitStatus({
      status: kamishibaiToLstStatus(symbol),
      justificativaDesvio: null,
      ...submitExtras,
    });
    if (ok && isAtaMode && symbol === 'O' && ataNotes.trim().length >= 10 && onSaveAta) {
      await onSaveAta({ notes: ataNotes.trim(), lideradoUserId: lideradoId || null, markRealizado: true });
    }
    if (ok) onClose();
  };

  const handleDeviationSubmit = async (
    type: 'text' | 'quick_action' | 'critical_problem',
    payload?: QuickActionPayload,
  ) => {
    const base = {
      status: 'realizado_com_desvio' as LstStatus,
      justificativaDesvio:
        type === 'text'
          ? deviationText.trim()
          : payload?.whatToDo ?? null,
      indicatorRealizedValue: requiresIndicator
        ? Number(indicatorInput.replace(',', '.')) || null
        : null,
      ...submitExtras,
    };
    if (type === 'quick_action' && payload) {
      const ok = await onSubmitStatus({ ...base, quickAction: payload });
      if (ok) onClose();
      return;
    }
    if (type === 'critical_problem' && payload) {
      const ok = await onSubmitStatus({
        ...base,
        criticalProblem: payload as unknown as CriticalProblemPayload,
      });
      if (ok) onClose();
      return;
    }
    const ok = await onSubmitStatus(base);
    if (ok) onClose();
  };

  const handleProcessStopped = async (motivo: LstMotivoParada) => {
    const ok = await onSubmitStatus({ status: 'processo_parado', motivoParada: motivo, ...submitExtras });
    if (ok) onClose();
  };

  const handleSaveAtaOnly = async () => {
    if (!onSaveAta || ataNotes.trim().length < 10) return;
    const ok = await onSaveAta({
      notes: ataNotes.trim(),
      lideradoUserId: lideradoId || null,
      markRealizado: false,
    });
    if (ok) onClose();
  };

  return (
    <div className="fixed inset-0 z-[9998] flex justify-end">
      <button
        type="button"
        aria-label="Fechar"
        onClick={onClose}
        className="absolute inset-0 bg-slate-900/40 backdrop-blur-[1px] transition-opacity"
      />

      <aside
        role="dialog"
        aria-modal="true"
        className={`relative flex h-full w-full max-w-[480px] flex-col border-l border-slate-200 bg-white shadow-2xl transition-transform duration-200 ease-out dark:border-slate-800 dark:bg-[var(--bg-surface)] border-l-4 ${LST_CATEGORY_STYLES[category].border}`}
      >
        <header className="shrink-0 border-b border-slate-100 px-4 py-3 dark:border-slate-800">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0 flex-1">
              <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">
                {isAtaMode ? 'Ata Coletiva · Camada' : 'Instrução de Trabalho Padrão'}
              </p>
              <h2 className="text-[16px] font-bold leading-tight text-slate-900 dark:text-slate-50">
                {rotina.template.title}
              </h2>
              <div className="mt-1.5 flex flex-wrap items-center gap-2">
                <span className="inline-flex items-center gap-1 rounded-md bg-slate-100 px-2 py-0.5 text-[11px] font-semibold text-slate-600 dark:bg-slate-800 dark:text-slate-300">
                  <Clock size={11} />
                  {formatTimeHHmm(rotina.template.scheduled_time) ?? 'Sem horário'}
                </span>
                <RotinaStatusBadge
                  status={displayStatus === 'pendente' ? 'agendado' : displayStatus}
                  justificativa={rotina.execucao?.justificativa_desvio}
                />
                <span className="text-[10px] text-slate-400">
                  {LST_FREQUENCY_LABELS[rotina.template.frequency]}
                </span>
              </div>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg p-2 text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800"
              aria-label="Fechar"
            >
              <X size={18} />
            </button>
          </div>
        </header>

        <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4">
          {isAtaMode ? (
            <>
              <section className="mb-4">
                <h3 className="mb-2 flex items-center gap-1.5 text-[11px] font-bold uppercase text-slate-500">
                  <FileText size={13} className="text-violet-500" /> Ata Coletiva
                </h3>
                {liderados.length > 0 && (
                  <div className="mb-2">
                    <label className={labelCls}>Liderado em foco</label>
                    <select className={inputCls} value={lideradoId} onChange={(e) => setLideradoId(e.target.value)}>
                      <option value="">— Geral da camada —</option>
                      {liderados.map((l) => (
                        <option key={l.id} value={l.id}>{l.name}</option>
                      ))}
                    </select>
                  </div>
                )}
                <textarea
                  className="min-h-[100px] w-full resize-y rounded-lg border border-slate-200 px-3 py-2 text-[12px] leading-relaxed outline-none focus:border-violet-400 dark:border-slate-700 dark:bg-slate-900/40"
                  value={ataNotes}
                  onChange={(e) => setAtaNotes(e.target.value)}
                  placeholder="Decisões, obstáculos, escalonamentos e compromissos do gemba…"
                />
                {onSaveAta && (
                  <button
                    type="button"
                    disabled={savingAta || ataNotes.trim().length < 10}
                    onClick={() => void handleSaveAtaOnly()}
                    className="mt-2 inline-flex w-full items-center justify-center gap-1.5 rounded-md bg-violet-600 px-3 py-2 text-[11px] font-semibold text-white disabled:opacity-40"
                  >
                    {savingAta ? <Loader2 size={13} className="animate-spin" /> : <Users size={13} />}
                    Salvar ata (sem alterar status)
                  </button>
                )}
              </section>

              {desviosDoDia.length > 0 && (
                <section className="mb-4">
                  <h3 className="mb-2 flex items-center gap-1.5 text-[11px] font-bold uppercase text-slate-500">
                    <AlertTriangle size={13} className="text-amber-500" /> Desvios do turno
                  </h3>
                  <ul className="space-y-1.5">
                    {desviosDoDia.map((d, i) => (
                      <li
                        key={`${d.title}-${i}`}
                        className="rounded-md border border-amber-100 bg-amber-50/60 px-2.5 py-2 dark:border-amber-900/40 dark:bg-amber-950/20"
                      >
                        <p className="text-[11px] font-semibold text-slate-800 dark:text-slate-100">{d.title}</p>
                        {d.justificativa && (
                          <p className="mt-0.5 text-[10px] italic text-amber-700 dark:text-amber-300">{d.justificativa}</p>
                        )}
                      </li>
                    ))}
                  </ul>
                </section>
              )}

              <section>
                <h3 className="mb-2 flex items-center gap-1.5 text-[11px] font-bold uppercase text-slate-500">
                  <History size={13} /> Histórico de atas
                </h3>
                {revisoesLoading ? (
                  <p className="text-[11px] text-slate-400">Carregando…</p>
                ) : historico.length === 0 ? (
                  <p className="text-[11px] italic text-slate-400">Nenhuma ata anterior.</p>
                ) : (
                  <ul className="max-h-[140px] space-y-1.5 overflow-y-auto">
                    {historico.map((r) => (
                      <li key={r.id} className="rounded border border-slate-100 px-2 py-1.5 dark:border-slate-800">
                        <p className="text-[9px] text-slate-400">
                          {new Date(r.created_at).toLocaleDateString('pt-BR')} · {resolveUserName(r.reviewer_user_id)}
                        </p>
                        <p className="text-[10px] text-slate-600 dark:text-slate-300 line-clamp-2">{r.notes}</p>
                      </li>
                    ))}
                  </ul>
                )}
              </section>
            </>
          ) : (
            <SwiExecutionSections
              template={rotina.template}
              indicatorMeta={indicatorMeta}
              indicatorAssessment={linkedAssessment}
              observacao={observacaoText}
              achados={deviationText}
              onObservacaoChange={setObservacaoText}
              onAchadosChange={setDeviationText}
            />
          )}

          {requiresIndicator && pendingSymbol === 'O' && (
            <div className="mt-4 rounded-lg border border-blue-200 bg-blue-50/50 p-3 dark:border-blue-900/40">
              <label className={labelCls}>
                Valor {indicatorMeta?.name}
                {indicatorMeta?.target != null ? ` (meta: ${indicatorMeta.target}${indicatorMeta.unit})` : ''}
              </label>
              <input
                type="number"
                step="any"
                value={indicatorInput}
                onChange={(e) => setIndicatorInput(e.target.value)}
                className="h-9 w-full rounded-md border border-slate-200 px-2 text-[13px] dark:border-slate-700 dark:bg-slate-900/50"
              />
            </div>
          )}

          {pendingSymbol === 'triangle' && (
            <div ref={deviationRef} className="mt-4 space-y-3">
              <div>
                <label className={labelCls}>Anomalia detectada (Δ)</label>
                <textarea
                  autoFocus
                  value={deviationText}
                  onChange={(e) => setDeviationText(e.target.value)}
                  rows={3}
                  className="w-full resize-none rounded-lg border border-amber-200 bg-amber-50/30 px-3 py-2 text-[12px] outline-none focus:border-amber-400 dark:border-amber-900/50 dark:bg-amber-950/20"
                  placeholder="Ex.: inseguro, rebarba, falta de material…"
                />
                <button
                  type="button"
                  disabled={saving || deviationText.trim().length < 3}
                  onClick={() => void handleDeviationSubmit('text')}
                  className="mt-2 w-full rounded-lg bg-amber-500 py-2 text-[12px] font-semibold text-white disabled:opacity-40"
                >
                  Registrar Desvio (Δ)
                </button>
                <button
                  type="button"
                  onClick={() => setPendingSymbol(null)}
                  className="mt-1.5 w-full py-1 text-[11px] text-slate-400 hover:text-slate-600"
                >
                  Voltar
                </button>
              </div>
              <NonConformityEscalationPanel
                rotinaTitle={rotina.template.title}
                members={members}
                defaultAssigneeId={userId}
                saving={saving}
                onCancel={() => setPendingSymbol(null)}
                onSubmitQuickAction={(p) => void handleDeviationSubmit('quick_action', p)}
                onSubmitCriticalProblem={(p) => void handleDeviationSubmit('critical_problem', p)}
              />
            </div>
          )}
        </div>

        {pendingSymbol !== 'triangle' && (
          <footer className="shrink-0 border-t border-slate-200 bg-slate-50/95 p-4 pb-[max(1rem,env(safe-area-inset-bottom))] dark:border-slate-800 dark:bg-slate-900/95">
            <div className="mb-3 rounded-xl border-2 border-slate-200 bg-white px-3 py-2.5 dark:border-slate-700 dark:bg-slate-900/60">
              <p className="text-center text-[11px] font-bold uppercase tracking-wide text-slate-700 dark:text-slate-200">
                Resultado da verificação
              </p>
              <p className="mt-0.5 text-center text-[10px] text-slate-400">
                Após revisar o SWI, registre a decisão da auditoria
              </p>
            </div>
            <div className="flex flex-wrap items-stretch gap-2">
              <KamishibaiActionButton
                symbol="O"
                label="O · Conforme"
                active={currentSymbol === 'O'}
                disabled={saving}
                onClick={() => void handleSymbolSelect('O')}
              />
              <KamishibaiActionButton
                symbol="triangle"
                label="Δ · Desvio"
                active={currentSymbol === 'triangle'}
                disabled={saving}
                onClick={() => setPendingSymbol('triangle')}
              />
              <div className="flex min-h-[72px] flex-1 flex-col items-center justify-center gap-1 rounded-xl border-2 border-dashed border-slate-300 px-2 py-2 dark:border-slate-600">
                <ProcessStoppedControl
                  active={isParado}
                  motivo={motivoParada}
                  disabled={saving}
                  onSelect={(m) => void handleProcessStopped(m)}
                />
                {isParado && motivoParada && (
                  <ProcessStoppedBadge motivo={motivoParada} size="sm" />
                )}
              </div>
            </div>
            {currentSymbol === 'X' && (
              <p className="mt-2 flex items-center justify-center gap-1.5 text-[10px] text-red-600">
                <KamishibaiSymbolBadge symbol="X" size="sm" />
                Não realizado — estouro de tempo
              </p>
            )}
          </footer>
        )}
      </aside>
    </div>
  );
};

export default RotinaContextDrawer;
